import { ArrowRight } from "lucide-react";

// The Journey So Far — a narrative chapter replacing the Team section.
// Content is authored below so it stays close to the layout that reads it.

const ACTIVITIES = [
  {
    number: "01",
    title: "Launch Event & Panel Conversation",
    place: "Common House · Richmond, Virginia",
    paragraphs: [
      "At Common House in Richmond, we officially introduced ARC Institute Global. The evening featured a powerful panel conversation about the transformative journey from Prison to Palace, connecting Senegal's democratic movement to a new home in Richmond.",
      "Guests were deeply engaged, spending time with the printed photographs that tell this story. The energy in the room was one of shared purpose and growing excitement.",
    ],
    images: [
      {
        src: "/journey/01-launch-panel.jpg",
        alt: "Panel conversation at the ARC Institute Global launch event at Common House, Richmond",
      },
      {
        src: "/journey/01-launch-prints.jpg",
        alt: "A guest intently viewing printed photographs at the ARC Institute Global launch event",
      },
    ],
  },
  {
    number: "02",
    title: "Art & Civic Engagement with KIPP DC Students",
    place: "KIPP DC · Washington",
    paragraphs: [
      "We visited KIPP DC's art department to talk about how art can contribute to bringing about change. Students asked thoughtful questions about the exhibition, Senegal's democratic movement, and how they can use their creative voices to make a difference.",
      "The visit was a powerful reminder that this work is not just about the past. It's about inspiring the next generation of civic leaders.",
    ],
    images: [
      {
        src: "/journey/02-kipp-classroom.jpg",
        alt: "ARC Institute team engaging with students from the KIPP DC art department",
      },
      {
        src: "/journey/02-kipp-group.jpg",
        alt: "Group photo of the ARC Institute team with KIPP DC students and their artwork",
      },
    ],
  },
  {
    number: "03",
    title: "Building Partnerships in Washington DC",
    place: "Washington, DC",
    paragraphs: [
      "In Washington DC, we met with institutional partners to discuss the project's vision and explore collaboration. These conversations laid the groundwork for partnerships that will help bring Première Ligne to a wider audience.",
    ],
    images: [
      {
        src: "/journey/03-dc-partners.jpg",
        alt: "Meeting with institutional partners and supporters in Washington DC",
      },
    ],
  },
  {
    number: "04",
    title: "A Night of Vision and Connection at The Grove",
    place: "The Grove Inn",
    paragraphs: [
      "At The Grove, we gathered with supporters for an intimate dinner. Guests asked questions, shared their own experiences, and connected the story of Senegal's democratic movement to their American experience. It was a night of deep listening and growing excitement for what's to come.",
    ],
    images: [
      {
        src: "/journey/04-grove-dinner.jpg",
        alt: "Intimate dinner conversation with supporters at The Grove Inn",
      },
    ],
  },
];

function ActivityImage({ image, index }) {
  return (
    <figure className="relative">
      <div className="aspect-[3/2] w-full overflow-hidden bg-[#111] outline outline-1 outline-[#F5F0E8]/8">
        <img
          src={image.src}
          alt={image.alt}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover pl-no-bw transition-transform duration-1000 hover:scale-[1.02]"
        />
      </div>
      <figcaption className="sr-only">{image.alt}</figcaption>
      {index !== undefined && (
        <span
          aria-hidden="true"
          className="absolute top-3 left-3 text-[10px] font-mono tracking-[0.25em] text-[#F5F0E8] bg-[#0A0A0A]/70 px-2 py-1 backdrop-blur-sm"
        >
          {index}
        </span>
      )}
    </figure>
  );
}

function Activity({ activity, i }) {
  const hasTwoImages = activity.images.length === 2;
  const flipped = i % 2 === 1;

  return (
    <article
      data-testid={`journey-activity-${i}`}
      className="relative border-t pl-hairline pt-10 md:pt-14"
    >
      {/* Chapter meta */}
      <header className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mb-8 md:mb-12">
        <div className="md:col-span-3 flex items-baseline gap-4">
          <span className="font-serif-display text-4xl md:text-5xl text-[#C41E1E] font-bold leading-none">
            {activity.number}
          </span>
          <span className="text-[10px] uppercase tracking-[0.3em] pl-mute">
            May 2026
          </span>
        </div>
        <div className="md:col-span-9">
          <h3 className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#F5F0E8]">
            {activity.title}
          </h3>
          <p className="mt-3 text-[11px] uppercase tracking-[0.28em] text-[#F5F0E8]/60">
            {activity.place}
          </p>
        </div>
      </header>

      {/* Body */}
      {hasTwoImages ? (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="md:col-span-7 space-y-10">
            <ActivityImage image={activity.images[0]} index="I" />
            <p className="text-base md:text-lg leading-relaxed text-[#F5F0E8]/85 max-w-2xl">
              {activity.paragraphs[0]}
            </p>
          </div>
          <div className="md:col-span-5 md:pt-16 space-y-6">
            <ActivityImage image={activity.images[1]} index="II" />
            {activity.paragraphs[1] && (
              <p className="text-base md:text-lg leading-relaxed text-[#F5F0E8]/80">
                {activity.paragraphs[1]}
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
          <div
            className={`md:col-span-7 ${flipped ? "md:order-2" : ""}`}
          >
            <ActivityImage image={activity.images[0]} />
          </div>
          <div
            className={`md:col-span-5 md:pt-6 ${flipped ? "md:order-1" : ""}`}
          >
            {activity.paragraphs.map((p, j) => (
              <p
                key={j}
                className={`text-base md:text-lg leading-relaxed text-[#F5F0E8]/85 ${
                  j > 0 ? "mt-5" : ""
                }`}
              >
                {p}
              </p>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

export default function JourneySoFar() {
  return (
    <section
      id="journey"
      data-testid="journey-section"
      className="relative pl-section-dark"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24">
        {/* Section header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-14 md:mb-20">
          <div className="lg:col-span-6">
            <span className="overline">Field Report</span>
            <h2 className="mt-4 font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[#F5F0E8]">
              The Journey <br />
              <span className="italic">So Far.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-base md:text-lg pl-mute leading-relaxed">
              In May 2026, we brought the vision to life, engaging students,
              community leaders, and supporters across Washington DC and
              Richmond. These moments mark the beginning of a story that will
              unfold at Chamberbrook in April 2027.
            </p>
          </div>
        </div>

        {/* Activities as chapters */}
        <div className="space-y-20 md:space-y-28">
          {ACTIVITIES.map((activity, i) => (
            <Activity key={activity.number} activity={activity} i={i} />
          ))}
        </div>

        {/* Closing */}
        <div className="mt-24 md:mt-32 border-t pl-hairline pt-12 md:pt-16 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <p
            className="font-serif-display italic text-2xl md:text-3xl lg:text-4xl leading-tight text-[#F5F0E8] max-w-3xl"
            data-testid="journey-closing"
          >
            This was just the beginning. The exhibition opens April 2, 2027,
            at Chamberbrook, Richmond.
          </p>
          <a
            href="#newsletter"
            data-testid="journey-stay-connected-btn"
            className="pl-cta pl-cta-primary shrink-0"
          >
            Stay Connected
            <ArrowRight size={14} strokeWidth={2.25} />
          </a>
        </div>
      </div>
    </section>
  );
}
